import React from "react";
import Title from "../../ui/Title";

const WhatIsCare = () => {
  return (
    <div id="what-is-care-economy" className="scroll-mt-24">
      <Title title="What is the care economy?" />

      <div className="space-y-4 text-softBlack leading-loose text-base font-normal mt-4">
        <p>
          The care economy refers to the economic activities related to
          providing care for individuals, and includes both paid and unpaid
          work. According to a{" "}
          <a
            href="https://www.ilo.org/sites/default/files/2024-07/ILC112-Resolution%20V-%7BRELMEETINGS-240620-001%7D-Web-EN_0.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            resolution
          </a>{" "}
          adopted by the International Labour Organisation (ILO) on June 14,
          2024, the care economy encompasses:
        </p>
        <ul className="list-decimal list-outside space-y-2 ml-6">
          <li>
            Care work that is both paid and unpaid; provided in the formal and
            informal sectors; indirect and direct; within and outside the
            household; and delivered by skilled personnel, family members or
            persons close to the person receiving care
          </li>
          <li>
            People who provide care and receive care, including children,
            adolescents, the elderly, sick and disabled
          </li>
          <li>Employers and institutions offering care services</li>
          <li>
            Policies and regulatory frameworks, services, infrastructure, and
            financing mechanisms that support caregiving
          </li>
          <li>Social norms that govern the provision and receipt of care</li>
        </ul>
        <p>
          In recent years, there has been an increasing focus on the global care
          economy. In the wake of a global pandemic, and in the face of an aging
          population, gendered care dynamics, and low labor force participation
          rates of women globally, international development organizations have
          been increasingly collaborating with governments to formulate action
          plans to strengthen their care economies. The Sustainable Development
          Goals, which came into effect in 2016, recognise this increased focus
          through Goal 5 on gender equality, which calls on member nations to
          recognize unpaid care and provide public services, infrastructure and
          social protection for caregivers.
        </p>
        <p>
          As early as 2018, the International Labour Organization (ILO)
          published a{" "}
          <a
            href="https://www.ilo.org/sites/default/files/wcmsp5/groups/public/%40dgreports/%40dcomm/%40publ/documents/publication/wcms_633135.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            report
          </a>{" "}
          titled Care Work and Care Jobs for the Future of Decent Work , which
          details how changing demography, women’s underrepresentation in the
          labor force, and gaps in care provision and policy could ‘create a
          severe and unsustainable global care crisis and increase gender
          inequalities at work’.
        </p>
        <p id="global-roadmap-care-economy" className="scroll-mt-24">
          The Global Roadmap for Action on the Care Economy,{" "}
          <a
            href="https://www.cgdev.org/sites/default/files/GlobalRoadmapOnCareFinal1.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            published
          </a>{" "}
          in 2024, lays out the key challenges facing the care economy,
          including:
        </p>
        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>
            A gendered imbalance in care responsibilities, with women and girls
            providing 76 percent of unpaid care work
          </li>
          <li>
            The undervaluation of paid care, resulting in lower wages, unsafe
            working conditions and gender and racial discrimination
          </li>
          <li>
            The limited access of a large segment of the population, especially
            in low-and middle-income countries, to high quality care services
          </li>
        </ul>
        <p id="wef-care-economy" className="scroll-mt-24">
          A{" "}
          <a
            href="https://www3.weforum.org/docs/WEF_The_Future_of_the_Care_Economy_2024.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            white paper
          </a>{" "}
          published by the World Economic Forum in March 2024 conceptualizes the
          care economy from three perspectives, and further highlights the need
          for governments, businesses, and community and civil society to work
          together to strengthen the care economy. These include:
        </p>
        <ul className="list-decimal list-outside space-y-2 ml-6">
          <li>
            A ‘macro’ perspective, which looks at the care economy through its
            potential economic outcomes, such as increased gross domestic
            product, workforce participation, and job creation
          </li>
          <li>
            A ‘business’ perspective, through which organizations can relate to
            the care economy as employers with a duty of care towards workers,
            as investors and innovators in the sector, and as providers of care
            services and goods
          </li>
          <li>
            A ‘rights-based’ perspective, which frames the care economy as a
            form of social organization for gender equality, demographic change,
            disability inclusion, global mobility, climate adaptation, and more
          </li>
        </ul>
        <p>
          According to a 2018 ILO report, the value of unpaid care and domestic
          work is estimated to be as much as 9 percent of the global GDP (USD 11
          trillion). In 2023, a consortium of development organizations released
          the Roadmap for Action on the Care Economy in Asia and the Pacific,
          based on an international gathering on the care economy in Bali. The{" "}
          <a
            href="https://asiafoundation.org/wp-content/uploads/2024/08/Roadmap-for-Action-on-the-Care-Economy-in-Asia-and-the-Pacific.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            roadmap
          </a>{" "}
          highlights the role of government, markets, the private sector, civil
          society, households, and families in strengthening the care ecosystem
          and addressing issues of paid and unpaid care, and poor care
          infrastructure, taking into account the intersectionalities of climate
          change, migration, conflict and gender-based violence.
        </p>
        <p>
          For a brief yet comprehensive view of the care economy, watch{" "}
          <a
            href="https://www.youtube.com/watch?v=ed8kOnv0aWw"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            this video
          </a>{" "}
          by the ILO.
        </p>
      </div>
    </div>
  );
};

export default WhatIsCare;
